"use client";

import React, { useMemo } from "react";
import { Loader, Tag, Folder, Share2 } from "lucide-react";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";

declare global {
  interface Window {
    wvcClient: any;
  }
}

function SingleProductContent() {
  const { products, loading, error } = useEcommerceProducts();

  if (loading) {
    return (
      <div className="flex min-h-[60vh] w-full items-center justify-center">
        <Loader className="text-primary size-10 animate-spin" />
      </div>
    );
  }

  if (error || !products || products.length === 0) {
    return (
      <div className="flex min-h-[60vh] w-full flex-col items-center justify-center gap-6 text-center px-4">
        <h2 className="text-3xl font-bold text-foreground">Product Not Found</h2>
        <p className="text-muted-foreground max-w-md text-lg">
          The product you are looking for does not exist or has been moved.
        </p>
        <Link to="/">
          <Button variant="default" size="lg" className="rounded-full px-8">
            Return to Home
          </Button>
        </Link>
      </div>
    );
  }

  const product = products[0];

  return (
    <div className="container mx-auto max-w-7xl px-4 py-12 md:py-24">
      {}
      <div className="mb-12 text-center md:mb-16">
        <h1 className="text-4xl font-bold tracking-tight text-primary md:text-5xl lg:text-6xl">
          {product.title}
        </h1>
      </div>

      <div className="grid gap-12 lg:grid-cols-2 lg:gap-20 items-start">
        {}
        <div className="relative w-full">
          <div className="aspect-square w-full overflow-hidden rounded-2xl bg-secondary/20 shadow-sm ring-1 ring-black/5">
            {product.featured_image_url ? (
              <img
                src={product.featured_image_url}
                alt={product.title}
                className="h-full w-full object-cover object-center transition-transform duration-700 hover:scale-105"
                loading="eager"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
                <span className="text-lg font-medium">No Image Available</span>
              </div>
            )}
          </div>
        </div>

        {}
        <div className="flex flex-col gap-8 pt-2">
          {}
          <div className="flex items-baseline gap-4">
            {product.discounted_price ? (
              <>
                <span className="text-4xl font-bold text-primary md:text-5xl">
                  ${product.discounted_price}
                </span>
                <span className="text-2xl text-muted-foreground line-through decoration-2">
                  ${product.price}
                </span>
              </>
            ) : (
              <span className="text-4xl font-bold text-primary md:text-5xl">
                ${product.price}
              </span>
            )}
          </div>

          {}
          {product.short_description && (
            <div
              className="prose prose-neutral prose-lg max-w-none text-muted-foreground leading-relaxed"
              dangerouslySetInnerHTML={{ __html: product.short_description }}
            />
          )}

          <Separator className="bg-border/60" />

          {}
          <div className="flex flex-col gap-6">
            <AddToCartButton
              productId={product.id}
              variant="default"
              size="lg"
              showQuantity={true}
              text="Add to Cart"
              buttonClassName="bg-primary text-primary-foreground hover:bg-primary/90 w-full md:w-auto rounded-full px-10 py-7 text-lg font-semibold transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5"
              className="w-full"
            />
            <p className="text-xs text-muted-foreground text-center md:text-left px-2">
              Free shipping on orders over $1000. Secure checkout.
            </p>
          </div>

          <Separator className="bg-border/60" />

          {}
          {product.long_description && (
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="description" className="border-border/60">
                <AccordionTrigger className="text-lg font-medium text-foreground hover:text-primary hover:no-underline py-4">
                  Product Description
                </AccordionTrigger>
                <AccordionContent>
                  <div
                    className="prose prose-neutral max-w-none pt-4 text-muted-foreground leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: product.long_description }}
                  />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}

          {}
          <div className="mt-4 space-y-5 rounded-xl bg-secondary/30 p-6">
            {product.categories && product.categories.length > 0 && (
              <div className="flex flex-wrap items-center gap-3">
                <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
                  <Folder className="size-4 text-primary" />
                  Categories:
                </span>
                <div className="flex flex-wrap gap-2">
                  {product.categories.map((cat, i) => (
                    <Link
                      key={cat.id}
                      to={cat.link}
                      data-index={i}
                      className="rounded-full bg-background px-3 py-1 text-xs font-medium text-foreground shadow-sm transition-colors hover:bg-primary hover:text-primary-foreground"
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {product.tags && product.tags.length > 0 && (
              <div className="flex flex-wrap items-center gap-3">
                <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
                  <Tag className="size-4 text-primary" />
                  Tags:
                </span>
                <div className="flex flex-wrap gap-2">
                  {product.tags.map((tag, i) => (
                    <Link
                      key={tag.id}
                      to={tag.link}
                      data-index={i}
                      className="text-sm text-muted-foreground hover:text-primary transition-colors underline-offset-4 hover:underline"
                    >
                      #{tag.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SingleProductTemplateSection() {
  const wpQuery = useMemo(() => {
    if (typeof window !== "undefined" && window.wvcClient) {
      return new WP_Query(window.wvcClient.getCurrentWPQueryParams());
    }
    return new WP_Query({ post_type: "product" });
  }, []);

  return (
    <section id="single-product-template-section" className="bg-background w-full">
      <EcommerceProductsProvider wp_query={wpQuery} postType="product">
        <SingleProductContent />
      </EcommerceProductsProvider>
    </section>
  );
}